import React, { useState, useEffect } from "react";
import styles from "./Auth.module.scss";
import { useLogin, useCommunity } from "../../context/community";
import { NavLink, Link, useLocation } from "react-router-dom";
import { PAGES } from "../../routes/PAGES";
import LangService from "../../services/LangService";
import Input from "../../components/Input/Input";
import Button from "../../components/Button/Button";
import validation from "./validation";
import { AUTH_TYPES } from "../../context/AUTH_TYPES";
import OAuthLogin from "./components/OAuthLogin";
import { useGoogleLogin } from "./GoogleLogin/useGoogleLogin";
import { GoogleLogin } from "./GoogleLogin/GoogleLogin";
import { useOneTimeCode } from "./OneTimeCode/useOneTimeCode";
import { ShortAuth } from "./ShortAuth/ShortAuth";
import cn from "classnames";
import { LOGIN_ERROR_CODES, LOGIN_ERROR_STORAGE_KEYS } from "../../constants/loginErrors";
import { resolveCommunityLogo } from "services/themePalette";

const Login = () => {
    const GTAG_EVENT = 'login'
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [errorText, setErrorText] = useState("");
    const [communityRedirectUrl, setCommunityRedirectUrl] = useState("");
    const [loading, setLoading] = useState(false);
    const [redirectTo, setRedirectTo] = useState(null);
    const login = useLogin();
    const location = useLocation();
    const { community: { authConfig, logoUrl, darkLogoUrl, domain }, themeMode } = useCommunity();
    const brandLogo = resolveCommunityLogo({ mode: themeMode, logoUrl, darkLogoUrl });
    const authMap = LangService.map.pages.Auth;
    const langMap = LangService.map.pages.login;
    const errorsMap = LangService.map.errors;
    const hasEmail = authConfig?.findIndex(conf => conf.authType === AUTH_TYPES.EMAIL) != -1;
    const { hasGoogle, handleGoogleSuccess, handleGoogleFailure } = useGoogleLogin({
        gtagEvent: GTAG_EVENT,
        setErrorText
    });
    const { hasCodeAuth } = useOneTimeCode();
    const hasOAuth2 = authConfig && authConfig.findIndex(conf => conf.authType === "OAUTH2") != -1;

    useEffect(() => {
        const intendedPath = sessionStorage.getItem('redirectToAfterLogin');
        if (intendedPath) {
            setRedirectTo(intendedPath);
        }
        const stateLoginErrorCode = location.state?.loginErrorCode;
        const stateLoginErrorCommunityUrl = location.state?.loginErrorCommunityUrl;
        const loginErrorCode = stateLoginErrorCode || sessionStorage.getItem(LOGIN_ERROR_STORAGE_KEYS.code);
        const loginError = sessionStorage.getItem(LOGIN_ERROR_STORAGE_KEYS.message);
        const loginErrorCommunityUrl = stateLoginErrorCommunityUrl || sessionStorage.getItem(LOGIN_ERROR_STORAGE_KEYS.communityUrl);

        if (loginErrorCode === LOGIN_ERROR_CODES.TELEGRAM_NOT_LINKED) {
            setErrorText(langMap.telegramNotLinkedError || 'Telegram account is not linked. Log in with email to continue.');
            setCommunityRedirectUrl("");
        } else if (loginError) {
            setErrorText(loginError);
            setCommunityRedirectUrl("");
        }
        sessionStorage.removeItem(LOGIN_ERROR_STORAGE_KEYS.code);
        sessionStorage.removeItem(LOGIN_ERROR_STORAGE_KEYS.message);
        sessionStorage.removeItem(LOGIN_ERROR_STORAGE_KEYS.communityUrl);
        // Clear the Telegram auto-login failed flag when user is on login page
        // This allows them to try again after manually logging in
        sessionStorage.removeItem('telegramAutoLoginFailed');
        // It's generally better to remove the item after successful login/registration
        // or if the user navigates away without completing auth.
        // For now, we'll clear it after a login attempt.
    }, [location.key, location.state]);

    const handleError = () => {
        setErrorText("");
        const error = validation.email(email) || validation.password(password);
        if (error) {
            setErrorText(error);
        }

        return Boolean(error);
    };

    const loginSubmit = async () => {
        try {
            const authContext = {
                authType: AUTH_TYPES.EMAIL,
                email: email,
                password: password,
                token: null,
                backTo: redirectTo,
                introFor: null
            }
            await login(authContext);
            if (redirectTo) {
                sessionStorage.removeItem('redirectToAfterLogin');
            }
        } catch (e) {
            if (e.msg === "Invalid login or password") {
                e.msg = errorsMap[1011];
            }
            if (e.msg === "Access Denied") {
                e.msg = errorsMap[1005];
            }
            setErrorText(e.msg);
        }
    };

    const handleSubmit = async () => {
        if (handleError()) {
            return;
        }
        setLoading(true);
        gtag("event", GTAG_EVENT, {
            method: "Email",
        });
        await loginSubmit();
        setLoading(false);
    };

    const handleKeyDown = (event) => {
        if (event.keyCode === 13) {
            return handleSubmit();
        }
    };

    return (
        <div className={styles.page}>
            <div className={styles.container}>
                <div className={styles.brandLogo}>
                    <NavLink to={PAGES.WELCOME}>
                        <img src={brandLogo} alt="brand" width="200" />
                    </NavLink>
                </div>
                {domain !== "connect-entire" && (<>
                    <div className={styles.loginText}>{langMap.title}</div>
                    <div className={styles.loginSubText}>{langMap.subtitle}</div>
                </>)}
                {domain === "connect-entire" && (<>
                    <br />
                    <br />
                    <div className={styles.loginSubText}>{langMap.title}</div>
                </>)}
                {hasGoogle && !hasCodeAuth && (
                    <GoogleLogin
                        onGoogleSuccess={handleGoogleSuccess}
                        onGoogleFailure={handleGoogleFailure}
                    />
                )}
                {hasCodeAuth && (
                    <ShortAuth gtagEvent={GTAG_EVENT} />
                )}
                {hasOAuth2 && <OAuthLogin config={authConfig} />}
                {errorText && (
                    <div className={styles.errorText}>
                        {errorText}
                        {communityRedirectUrl && (
                            <>
                                <br />
                                <a href={communityRedirectUrl} className={styles.link}>
                                    {langMap.openCommunityLink || 'Open your community'}
                                </a>
                            </>
                        )}
                    </div>
                )}
                {hasEmail && <>
                    {authConfig?.length > 1 &&
                        <div className={styles.orContinueWith}>
                            <span>{authMap.orContinueWith}</span>
                        </div>}
                    <Input
                        label={authMap.email}
                        className={styles.input}
                        type="email"
                        value={email}
                        onChange={setEmail}
                        onKeyDown={handleKeyDown}
                        name="email"
                        autoComplete="email"
                        required
                        autoFocus
                    />
                    <Input
                        label={authMap.password}
                        className={styles.input}
                        type="password"
                        value={password}
                        onChange={setPassword}
                        onKeyDown={handleKeyDown}
                        name="password"
                        autoComplete="current-password"
                        required
                    />
                    <Button
                        wide
                        type="button"
                        onClick={handleSubmit}
                        style={styles.submitButton}
                        loading={loading}
                        active
                    >
                        {langMap.button}
                    </Button>
                </>}

                <p className={cn(styles.bottomQuestion, {
                    [styles.shortAuth]: hasCodeAuth
                })}>
                    {langMap.accountQuestion}
                    <br />
                    {domain !== "connect-entire" && (
                        <Link to={PAGES.REGISTRATION} className={styles.link}>
                            {langMap.link}
                        </Link>
                    )}
                    {domain === "connect-entire" && (
                        <a href={"https://my.entire.vc/apply-connect/"} className={styles.link}>
                            Apply now
                        </a>
                    )}
                </p>
                {hasEmail &&
                    (<p className={styles.bottomQuestion}>
                        {langMap.recoveryQuestion}
                        <Link to={PAGES.RECOVERY} className={styles.link}>
                            {langMap.recovery}
                        </Link>
                    </p>)}
            </div>
        </div>
    );
};

export default Login;
